<template>
  <AppNavbar />
  <div class="vx-admin">

    <div v-if="cargando" class="vx-panel vx-vacio">
      <div class="spinner-border spinner-border-sm text-secondary" role="status"></div>
      <span class="vx-vacio-texto">Cargando…</span>
    </div>

    <div v-else-if="error" class="vx-panel vx-vacio">
      <i class="bi bi-exclamation-triangle" style="font-size: 22px; color: var(--vx-texto-4)"></i>
      <span class="vx-vacio-titulo">No se pudo abrir el esquema</span>
      <span class="vx-vacio-texto">{{ error }}</span>
      <router-link class="vx-btn vx-btn-sm mt-1" :to="{ name: 'Esquemas' }">
        Volver al listado
      </router-link>
    </div>

    <template v-else-if="esquema">
      <div class="d-flex align-items-start justify-content-between mb-3">
        <div>
          <div class="vx-migas mb-1">
            <span>Cobranza</span><span class="sep">/</span>
            <router-link :to="{ name: 'Esquemas' }">Esquemas</router-link>
            <span class="sep">/</span><span class="vx-num">#{{ esquema.cr_esquema_id }}</span>
          </div>
          <div class="d-flex align-items-center gap-2 mb-1">
            <h1 class="vx-titulo">{{ esquema.nombre }}</h1>
            <span class="vx-estado" :class="esquema.activo ? 'vx-estado-activa' : 'vx-estado-neutro'">
              {{ esquema.activo ? 'Activo' : 'Inactivo' }}
            </span>
          </div>
          <div class="vx-ayuda">{{ periodicidadTexto(esquema) }}</div>
        </div>
        <div class="d-flex gap-2">
          <button v-if="puedeAdministrar" class="vx-btn" data-test="btn-duplicar"
                  @click="abrirDuplicado">
            <i class="bi bi-copy"></i> Duplicar
          </button>
          <button v-if="puedeAdministrar && editable" class="vx-btn" data-test="btn-editar-calendario"
                  @click="abrirEdicion">
            <i class="bi bi-pencil"></i> Editar
          </button>
          <button
            v-if="puedeGenerar"
            class="vx-btn vx-btn-primario"
            @click="$router.push({ name: 'Generacion', query: { esquema: esquema.cr_esquema_id } })"
          >
            <i class="bi bi-calendar-plus"></i> Generar período
          </button>
        </div>
      </div>

      <!-- Por qué un esquema deja de editarse. Se dice acá y no cuando el
           operador ya intentó guardar: la explicación después del rechazo
           llega tarde y se lee como una traba arbitraria. -->
      <div v-if="puedeAdministrar && !editable" class="vx-nota mb-3" data-test="aviso-congelado">
        <i class="bi bi-lock"></i>
        <!-- RN-170: el calendario, no los montos. Desde D45 cada concepto se
             corrige por lo que emitió él, desde su menú ⋮. -->
        <span>
          Este esquema ya generó cuotas, así que su <strong>calendario</strong> quedó fijo:
          moverlo contradiría lo que ya se emitió. Los conceptos sí se corrigen, desde el
          menú <i class="bi bi-three-dots-vertical"></i> de cada uno. Para el período
          siguiente, <strong>duplicalo</strong> — se copian los conceptos con sus montos
          y elegís desde cuándo arranca.
        </span>
      </div>

      <!-- Duplicar -->
      <div v-if="mostrarDuplicado" class="vx-panel mb-3" data-test="panel-duplicar">
        <div class="vx-panel-cabecera">
          <div>
            <h2 class="vx-panel-titulo">Duplicar esquema</h2>
            <div class="vx-ayuda mt-1">
              Se copian periodicidad, día de vencimiento y los {{ conceptosActivos }} conceptos
              activos con sus montos. Lo único nuevo es el nombre y desde cuándo arranca.
            </div>
          </div>
          <button class="vx-btn vx-btn-sm" @click="mostrarDuplicado = false">Cancelar</button>
        </div>
        <div class="p-3">
          <div class="row g-2">
            <div class="col-md-5">
              <label class="vx-etiqueta" for="dup-nombre">Nombre</label>
              <input id="dup-nombre" v-model.trim="duplicado.nombre" class="vx-campo"
                     placeholder="Ej.: Plan 2027">
            </div>
            <div class="col-md-4">
              <label class="vx-etiqueta" for="dup-inicio">Primer período</label>
              <input id="dup-inicio" v-model.trim="duplicado.periodo_inicio" class="vx-campo"
                     :placeholder="esquema.periodicidad_tipo === 'CALENDARIO' ? 'AAAA-MM' : 'AAAA-MM-DD'">
            </div>
            <div class="col-md-3">
              <label class="vx-etiqueta" for="dup-periodos">Períodos</label>
              <input id="dup-periodos" v-model.number="duplicado.cantidad_periodos" type="number"
                     min="1" class="vx-campo">
            </div>
          </div>
          <div v-if="errorDuplicado" class="vx-error mt-2" data-test="error-duplicar">
            {{ errorDuplicado }}
          </div>
          <div class="d-flex justify-content-end mt-3">
            <button class="vx-btn vx-btn-primario" :disabled="duplicando"
                    data-test="btn-confirmar-duplicar" @click="confirmarDuplicado">
              <span v-if="duplicando" class="spinner-border spinner-border-sm"></span>
              Duplicar
            </button>
          </div>
        </div>
      </div>

      <!-- Editar el calendario, sólo mientras no generó nada -->
      <div v-if="mostrarEdicion" class="vx-panel mb-3" data-test="panel-editar">
        <div class="vx-panel-cabecera">
          <div>
            <h2 class="vx-panel-titulo">Editar esquema</h2>
            <div class="vx-ayuda mt-1">
              Se puede porque todavía no generó ninguna cuota. Deja de poder en cuanto genere la primera.
            </div>
          </div>
          <button class="vx-btn vx-btn-sm" @click="mostrarEdicion = false">Cancelar</button>
        </div>
        <div class="p-3">
          <div class="row g-2">
            <div class="col-md-5">
              <label class="vx-etiqueta" for="ed-nombre">Nombre</label>
              <input id="ed-nombre" v-model.trim="edicion.nombre" class="vx-campo">
            </div>
            <div class="col-md-3">
              <label class="vx-etiqueta" for="ed-inicio">Primer período</label>
              <input id="ed-inicio" v-model.trim="edicion.periodo_inicio" class="vx-campo">
            </div>
            <div class="col-md-2">
              <label class="vx-etiqueta" for="ed-periodos">Períodos</label>
              <input id="ed-periodos" v-model.number="edicion.cantidad_periodos" type="number"
                     min="1" class="vx-campo">
            </div>
            <div v-if="esquema.periodicidad_tipo === 'CALENDARIO'" class="col-md-2">
              <label class="vx-etiqueta" for="ed-dia">Día vto.</label>
              <input id="ed-dia" v-model.number="edicion.dia_vencimiento" type="number"
                     min="1" max="31" class="vx-campo">
            </div>
          </div>
          <div v-if="errorEdicion" class="vx-error mt-2" data-test="error-editar">{{ errorEdicion }}</div>
          <div class="d-flex justify-content-end mt-3">
            <button class="vx-btn vx-btn-primario" :disabled="guardandoEdicion"
                    data-test="btn-guardar-edicion" @click="guardarEdicion">
              <span v-if="guardandoEdicion" class="spinner-border spinner-border-sm"></span>
              Guardar
            </button>
          </div>
        </div>
      </div>

      <div class="row g-3">
        <div class="col-lg-7">
          <!-- Conceptos: lo que se cobra en cada período (RN-9) -->
          <div class="vx-panel mb-3">
            <div class="vx-panel-cabecera">
              <div>
                <h2 class="vx-panel-titulo">Conceptos</h2>
                <div class="vx-ayuda mt-1">Qué se cobra en cada período</div>
              </div>
              <div class="d-flex align-items-center gap-3">
                <!-- RN-168: lo desactivado, oculto por defecto. -->
                <label v-if="conceptosDesactivados" class="vx-ayuda d-flex align-items-center gap-1"
                       style="cursor: pointer">
                  <input v-model="mostrarDesactivados" type="checkbox" data-test="mostrar-desactivados">
                  Mostrar desactivados ({{ conceptosDesactivados }})
                </label>
                <button
                  v-if="puedeAdministrar" class="vx-btn vx-btn-sm"
                  @click="mostrarAltaConcepto = !mostrarAltaConcepto"
                >
                  <i class="bi" :class="mostrarAltaConcepto ? 'bi-x-lg' : 'bi-plus-lg'"></i>
                  {{ mostrarAltaConcepto ? 'Cancelar' : 'Agregar' }}
                </button>
              </div>
            </div>

            <div v-if="mostrarAltaConcepto" class="p-3 border-bottom">
              <div class="row g-2">
                <div class="col-md-5">
                  <label class="vx-etiqueta" for="c-descripcion">Descripción</label>
                  <input
                    id="c-descripcion" v-model.trim="concepto.descripcion" class="vx-campo vx-campo-sm"
                    placeholder="Cuota mensual"
                    :class="{ 'vx-campo-error': errorConcepto('descripcion') }"
                  >
                  <div v-if="errorConcepto('descripcion')" class="vx-error">
                    {{ errorConcepto('descripcion') }}
                  </div>
                </div>
                <div class="col-md-3">
                  <label class="vx-etiqueta" for="c-tipo">Tipo</label>
                  <select id="c-tipo" v-model="concepto.tipo" class="vx-campo vx-campo-sm">
                    <option v-for="t in TIPOS_CONCEPTO" :key="t.valor" :value="t.valor">
                      {{ t.texto }}
                    </option>
                  </select>
                </div>
                <div class="col-md-2">
                  <label class="vx-etiqueta" for="c-monto">Monto</label>
                  <input
                    id="c-monto" v-model.number="concepto.monto" class="vx-campo vx-campo-sm vx-num"
                    type="number" min="0"
                    :class="{ 'vx-campo-error': errorConcepto('monto') }"
                  >
                  <div v-if="errorConcepto('monto')" class="vx-error">{{ errorConcepto('monto') }}</div>
                </div>
                <div class="col-md-2">
                  <label class="vx-etiqueta" for="c-iva">IVA</label>
                  <select id="c-iva" v-model.number="concepto.tipo_iva" class="vx-campo vx-campo-sm">
                    <option v-for="t in TIPOS_IVA" :key="t.valor" :value="t.valor">{{ t.texto }}</option>
                  </select>
                </div>
              </div>

              <div class="vx-ayuda mt-2">{{ notaTipoConcepto }}</div>

              <div v-if="errorAltaConcepto" class="vx-error mt-2">{{ errorAltaConcepto }}</div>

              <div class="d-flex justify-content-end mt-2">
                <button class="vx-btn vx-btn-sm" :disabled="guardandoConcepto" @click="agregarConcepto">
                  <span v-if="guardandoConcepto" class="spinner-border spinner-border-sm"></span>
                  Agregar concepto
                </button>
              </div>
            </div>

            <div v-if="!conceptos.length" class="vx-vacio">
              <i class="bi bi-tags" style="font-size: 22px; color: var(--vx-texto-4)"></i>
              <span class="vx-vacio-titulo">El esquema no cobra nada todavía</span>
              <span class="vx-vacio-texto">
                Sin conceptos, generar un período no crea ninguna cuota. Agregá al menos
                uno recurrente.
              </span>
            </div>

            <table v-else class="vx-tabla">
              <thead>
                <tr>
                  <th>Concepto</th>
                  <th>Tipo</th>
                  <th>IVA</th>
                  <th class="vx-col-num">Monto</th>
                  <th class="vx-col-num" title="Cuotas que ya generó este concepto">Cuotas</th>
                  <th style="width: 48px"></th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="c in conceptosVisibles" :key="c.cr_esquema_concepto_id"
                    :class="{ 'vx-fila-inactiva': !c.activo }" data-test="fila-concepto">
                  <td>
                    <div class="vx-celda-principal">{{ c.descripcion }}</div>
                    <div v-if="!c.activo" class="vx-celda-sub">Desactivado</div>
                  </td>
                  <td>
                    <span class="vx-estado" :class="claseTipoConcepto(c.tipo)">
                      {{ tipoConceptoTexto(c.tipo) }}
                    </span>
                  </td>
                  <td class="vx-ayuda">{{ ivaTexto(c.tipo_iva) }}</td>
                  <td class="vx-col-num vx-num">{{ formatearMonto(c.monto, esquema.moneda) }}</td>
                  <!-- RN-168: antes de abrir el menú, el operador ve si editar
                       va a pedir previsualización. -->
                  <td class="vx-col-num vx-num vx-ayuda" data-test="emitidas">{{ c.emitidas || '—' }}</td>
                  <td class="text-end">
                    <ConceptoAccionesMenu
                      :concepto="c" :puede-esquemas="puedeAdministrar" :puede-cargos="puedeCargos"
                      @accion="alElegirAccion"
                    />
                  </td>
                </tr>
              </tbody>
            </table>

            <div class="p-3 vx-nota">
              <i class="bi bi-info-circle"></i>
              <span>
                <strong>Editar</strong> recalcula las cuotas que no se cobraron.
                <strong>Desactivar</strong> deja de cobrarlo a los que se inscriban después.
                <strong>Eliminar</strong> borra las cuotas sin cobrar; si alguna ya se cobró, el
                concepto queda desactivado. Lo cobrado no se toca nunca.
              </span>
            </div>
          </div>
        </div>

        <div class="col-lg-5">
          <!-- Períodos. Vienen calculados por el backend con la MISMA función
               que usa la generación, así que esto no es una aproximación de la
               UI: es exactamente lo que se va a generar. -->
          <div class="vx-panel mb-3">
            <div class="vx-panel-cabecera">
              <h2 class="vx-panel-titulo">Períodos</h2>
              <span class="vx-ayuda vx-num">{{ periodos.length }}</span>
            </div>
            <div class="p-3">
              <div v-if="periodos.length" class="vx-ayuda mb-2">
                De {{ periodos[0].etiqueta }} a {{ periodos[periodos.length - 1].etiqueta }}.
                Primer vencimiento el {{ fechaCorta(periodos[0].fecha_vencimiento) }}.
              </div>
              <div class="d-flex flex-wrap gap-1">
                <span
                  v-for="p in periodos" :key="p.numero"
                  class="vx-chip vx-num"
                  :title="`Vence el ${fechaCorta(p.fecha_vencimiento)}`"
                >
                  {{ p.etiqueta }}
                </span>
              </div>
            </div>
          </div>

          <div class="vx-panel">
            <div class="vx-panel-cabecera"><h2 class="vx-panel-titulo">Resumen</h2></div>
            <div class="px-3 pb-2">
              <div v-for="d in resumen" :key="d.etiqueta"
                   class="d-flex justify-content-between gap-3 py-2 border-bottom">
                <span class="vx-ayuda">{{ d.etiqueta }}</span>
                <span style="font-size: 13px; font-weight: 550" class="text-end vx-num">{{ d.valor }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </template>

    <ConceptoEdicionModal
      v-if="editando" :concepto="editando" :moneda="esquema?.moneda || 'PYG'"
      @cerrar="editando = null" @guardado="alGuardar"
    />
    <ConceptoEliminacionModal
      v-if="eliminando" :concepto="eliminando" :moneda="esquema?.moneda || 'PYG'"
      @cerrar="eliminando = null" @eliminado="alEliminar"
    />
  </div>
</template>

<script setup>
// Detalle de un esquema: sus períodos y sus conceptos — CR-E2-006.
// SPEC RN-8, RN-9 · D5, D15, D19.

import { ref, reactive, computed, onMounted } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import AppNavbar from '@/components/AppNavbar.vue'
import ConceptoAccionesMenu from '@/components/ConceptoAccionesMenu.vue'
import ConceptoEdicionModal from '@/components/ConceptoEdicionModal.vue'
import ConceptoEliminacionModal from '@/components/ConceptoEliminacionModal.vue'
import { mensajeEdicion, mensajeEliminacion, mensajeReactivacion } from '@/utils/conceptoAcciones.mjs'
import { useNotificationStore } from '@/stores/notifications'
import { hasAnyPermission } from '@/services/authSessionService'
import {
  obtenerEsquema, actualizarEsquema, duplicarEsquema,
  listarConceptos, crearConcepto, desactivarConceptoPorTipo, reactivarConceptoPorTipo,
  TIPOS_IVA, TIPOS_CONCEPTO, ivaTexto, tipoConceptoTexto, periodicidadTexto,
  formatearMonto, fechaCorta
} from '@/services/cobranzaService'
import { validarConcepto } from '@/services/cobranzaSchemas'

const route = useRoute()
const router = useRouter()
const notificaciones = useNotificationStore()

const esquema = ref(null)
const periodos = ref([])
const conceptos = ref([])
const cargando = ref(true)
const error = ref('')
const mostrarAltaConcepto = ref(false)

// Un esquema es configuración hasta que genera su primera cuota; a partir de
// ahí es registro de lo emitido. Quién decide eso es el backend —es regla de
// dominio— y la pantalla solo obedece.
const editable = ref(false)

const mostrarDuplicado = ref(false)
const duplicando = ref(false)
const errorDuplicado = ref('')
const duplicado = reactive({ nombre: '', periodo_inicio: '', cantidad_periodos: null })

const mostrarEdicion = ref(false)
const guardandoEdicion = ref(false)
const errorEdicion = ref('')
const edicion = reactive({
  nombre: '', periodo_inicio: '', cantidad_periodos: null, dia_vencimiento: null
})

const conceptosActivos = computed(() => conceptos.value.filter((c) => c.activo).length)
const conceptosDesactivados = computed(() => conceptos.value.length - conceptosActivos.value)
const mostrarDesactivados = ref(false)
const conceptosVisibles = computed(() =>
  conceptos.value.filter((c) => c.activo || mostrarDesactivados.value))

// D45: el concepto sobre el que está abierto cada modal.
const editando = ref(null)
const eliminando = ref(null)

function abrirDuplicado () {
  errorDuplicado.value = ''
  mostrarEdicion.value = false
  // El nombre se propone, no se impone: casi siempre es el mismo con otro año.
  duplicado.nombre = ''
  duplicado.periodo_inicio = ''
  duplicado.cantidad_periodos = esquema.value?.cantidad_periodos ?? null
  mostrarDuplicado.value = true
}

async function confirmarDuplicado () {
  errorDuplicado.value = ''
  if (!duplicado.nombre) { errorDuplicado.value = 'Poné un nombre para el esquema nuevo.'; return }
  if (!duplicado.periodo_inicio) { errorDuplicado.value = 'Indicá desde qué período arranca.'; return }

  duplicando.value = true
  try {
    const r = await duplicarEsquema(esquema.value.cr_esquema_id, { ...duplicado })
    notificaciones.notify({
      type: 'success',
      message: `Esquema duplicado con ${r.conceptos_copiados} concepto(s).`
    })
    // Se va al nuevo: es sobre ese que va a seguir trabajando.
    router.push({ name: 'EsquemaDetalle', params: { id: r.esquema.cr_esquema_id } })
  } catch (err) {
    errorDuplicado.value = err?.userMessage || 'No se pudo duplicar el esquema.'
  } finally {
    duplicando.value = false
  }
}

function abrirEdicion () {
  errorEdicion.value = ''
  mostrarDuplicado.value = false
  edicion.nombre = esquema.value.nombre
  edicion.periodo_inicio = esquema.value.periodo_inicio
  edicion.cantidad_periodos = esquema.value.cantidad_periodos
  edicion.dia_vencimiento = esquema.value.dia_vencimiento
  mostrarEdicion.value = true
}

async function guardarEdicion () {
  errorEdicion.value = ''
  guardandoEdicion.value = true
  try {
    await actualizarEsquema(esquema.value.cr_esquema_id, { ...edicion })
    notificaciones.notify({ type: 'success', message: 'Esquema actualizado.' })
    mostrarEdicion.value = false
    await cargar()
  } catch (err) {
    errorEdicion.value = err?.userMessage || 'No se pudo actualizar el esquema.'
  } finally {
    guardandoEdicion.value = false
  }
}
const guardandoConcepto = ref(false)
const errorAltaConcepto = ref('')
const tocadoConcepto = ref(false)

const puedeAdministrar = computed(() => hasAnyPermission(['administrar_esquemas_cobro']))
const puedeGenerar = computed(() => hasAnyPermission(['generar_deudas']))
// RN-169: los cargos eventuales los administra su propio permiso (D38).
const puedeCargos = computed(() => hasAnyPermission(['administrar_cargos_eventuales']))

const concepto = reactive({ descripcion: '', tipo: 'RECURRENTE', monto: null, tipo_iva: 1 })

const erroresConcepto = computed(() => validarConcepto(concepto))
const errorConcepto = (campo) =>
  (tocadoConcepto.value ? erroresConcepto.value[campo] : '') || ''

const notaTipoConcepto = computed(() =>
  TIPOS_CONCEPTO.find((t) => t.valor === concepto.tipo)?.nota || ''
)

// Un cargo eventual se distingue de la cuota y de la matrícula: no lo paga
// todo el padrón, y desde esta pantalla no se edita ni se emite.
const claseTipoConcepto = (tipo) => {
  // `vx-estado-activa` y no una clase nueva: los modificadores definidos en
  // `admin.css` son tres —activa, neutro, atencion— y una cuarta inventada se
  // renderiza sin estilo, que es peor que reusar.
  if (tipo === 'EVENTUAL') return 'vx-estado-activa'
  if (tipo === 'UNICO') return 'vx-estado-atencion'
  return 'vx-estado-neutro'
}

// Lo que va a costar cada período, con los conceptos activos y antes de
// descuentos. Es la cifra que el administrador quiere ver para saber si
// configuró bien, y no la tiene en ningún otro lado.
const resumen = computed(() => {
  const activos = conceptos.value.filter((c) => c.activo)
  const suma = (t) => activos
    .filter((c) => c.tipo === t)
    .reduce((acc, c) => acc + Number(c.monto), 0)
  const recurrente = suma('RECURRENTE')
  const unico = suma('UNICO')
  const eventual = suma('EVENTUAL')
  const moneda = esquema.value?.moneda || 'PYG'
  const filas = [
    { etiqueta: 'Por período', valor: formatearMonto(recurrente, moneda) },
    { etiqueta: 'Sólo el primero (únicos)', valor: formatearMonto(unico, moneda) }
  ]
  // Su propia línea, y NO sumado al total del esquema: un cargo eventual no lo
  // paga todo el padrón ni se emite solo. Meterlo en «Total del esquema» diría
  // que cada familia va a pagar eso, que es falso (SPEC 7.2).
  if (eventual > 0) {
    filas.push({
      etiqueta: 'Cargos eventuales (a quien se le emita)',
      valor: formatearMonto(eventual, moneda)
    })
  }
  filas.push(
    {
      etiqueta: 'Total del esquema',
      valor: formatearMonto(recurrente * periodos.value.length + unico, moneda)
    },
    { etiqueta: 'Moneda', valor: moneda }
  )
  return filas
})

async function cargar () {
  cargando.value = true
  error.value = ''
  try {
    const r = await obtenerEsquema(route.params.id)
    esquema.value = r?.esquema || null
    periodos.value = r?.periodos || []
    editable.value = Boolean(r?.editable)
    conceptos.value = await listarConceptos(route.params.id)
  } catch (err) {
    error.value = err?.userMessage || 'Error inesperado.'
  } finally {
    cargando.value = false
  }
}

async function agregarConcepto () {
  tocadoConcepto.value = true
  errorAltaConcepto.value = ''
  if (Object.keys(erroresConcepto.value).length) return

  guardandoConcepto.value = true
  try {
    await crearConcepto(esquema.value.cr_esquema_id, {
      descripcion: concepto.descripcion,
      tipo: concepto.tipo,
      monto: concepto.monto,
      tipo_iva: concepto.tipo_iva,
      // El orden es el de carga: no se le pide al operador un número que no
      // tiene forma de elegir bien la primera vez.
      orden: conceptos.value.length
    })
    Object.assign(concepto, { descripcion: '', tipo: 'RECURRENTE', monto: null, tipo_iva: 1 })
    tocadoConcepto.value = false
    mostrarAltaConcepto.value = false
    conceptos.value = await listarConceptos(esquema.value.cr_esquema_id)
    notificaciones.notify({ type: 'success', message: 'Concepto agregado.' })
  } catch (err) {
    errorAltaConcepto.value = err?.userMessage || 'No se pudo agregar el concepto.'
  } finally {
    guardandoConcepto.value = false
  }
}

async function recargarConceptos () {
  conceptos.value = await listarConceptos(esquema.value.cr_esquema_id)
}

function alElegirAccion (accion, c) {
  if (accion === 'editar') editando.value = c
  else if (accion === 'eliminar') eliminando.value = c
  else if (accion === 'desactivar') desactivar(c)
  else if (accion === 'reactivar') reactivar(c)
}

// RN-181: confirmación con lo que va a volver a pasar. `await`: el confirm de
// la app devuelve una promesa (F-2).
async function reactivar (c) {
  const ok = await window.confirm(mensajeReactivacion(c), {
    title: '¿Reactivar el concepto?', confirmText: 'Sí, reactivar', cancelText: 'Cancelar'
  })
  if (!ok) return
  try {
    await reactivarConceptoPorTipo(c)
    await recargarConceptos()
    notificaciones.notify({ type: 'success', message: 'Concepto reactivado.' })
  } catch (err) {
    notificaciones.notify({
      type: 'error',
      message: err?.userMessage || 'No se pudo reactivar el concepto.'
    })
  }
}

async function alGuardar (r) {
  editando.value = null
  notificaciones.notify({ type: 'success', message: mensajeEdicion(r) })
  await recargarConceptos()
}

async function alEliminar (r) {
  eliminando.value = null
  notificaciones.notify({ type: 'success', message: mensajeEliminacion(r) })
  await recargarConceptos()
}

// RN-171: desactivar no toca cuotas, así que no pide previsualización.
async function desactivar (c) {
  try {
    await desactivarConceptoPorTipo(c)
    await recargarConceptos()
    notificaciones.notify({ type: 'success', message: 'Concepto desactivado.' })
  } catch (err) {
    notificaciones.notify({
      type: 'error',
      message: err?.userMessage || 'No se pudo desactivar el concepto.'
    })
  }
}

onMounted(cargar)
</script>
